
// ** Third Party Components
import { QueryClient } from "@tanstack/react-query"
import {
  Activity,
  BarChart3Icon,
  Building,
  ClipboardList,
  FilesIcon,
  HomeIcon,
  Notebook,
  Receipt,
  Users2,
} from "lucide-react"
import {
  ArrowRight,
  CalendarTick,
  ChartSquare,
  ClipboardClose,
  ClipboardText,
  CloseSquare,
  Convertshape,
  Cut,
  DocumentCopy,
  DocumentDownload,
  DocumentText1,
  DollarSquare,
  Edit2,
  Folder,
  FolderCloud,
  Grid6,
  Home2,
  Kanban,
  Link1,
  Map,
  Notification,
  Printer,
  Setting2,
  Share,
  TaskSquare,
  Trash,
  UserEdit,
  UserTick,
} from "iconsax-reactjs"

// ** Page Routes
import {
  ACTIVITIES_PAGE_ROUTE,
  ANALYTICS_PAGE_ROUTE,
  DASHBOARD_PAGE_ROUTE,
  FILES_PAGE_ROUTE,
  FINANCIAL_BOQ_ROUTE,
  FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE,
  FINANCIAL_PAGE_ROUTE,
  FINANCIAL_PRICE_LIST_PAGE_ROUTE,
  LOGIN_PAGE_ROUTE,
  NEW_DASHBOARD_PAGE_ROUTE,
  SETTING_PAGE_ROUTE,
  USER_PAGE_ROUTE,
  USERS_PAGE_ROUTE,
} from "@pma/utils/page-routes"

// ** Types
import { FolderFileContextParams, NotificationFormValues, Permission, ProjectFilters, ProjectSorting, SubTab } from "@pma/types"

export const ACCESS_TOKEN = "pma_token"
/** Role ID that grants admin privileges within an organization */
export const ADMIN_ROLE_ID = 22
export const APP_CONFIG = "pma_config"
export const LOCALE = "PMA_LOCALE"
export const TABS_CONFIG = "PMA_TABS"

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 60_000,
    },
  },
})

export const navOptions = [
  { label: "Home", href: DASHBOARD_PAGE_ROUTE, icon: HomeIcon },
  { label: "Activities", href: ACTIVITIES_PAGE_ROUTE, icon: Activity },
  { label: "Users", href: USERS_PAGE_ROUTE, icon: Users2 },
  { label: "Analytics", href: ANALYTICS_PAGE_ROUTE, icon: BarChart3Icon },
  // { label: "GIS", href: GIS_PAGE_ROUTE, icon: Map },
  {
    label: "Financial",
    href: FINANCIAL_PAGE_ROUTE,
    icon: Receipt,
    sub: [
      {
        label: "Bill of Quantities",
        href: FINANCIAL_BOQ_ROUTE,
        icon: Notebook,
      },
      {
        label: "Contractor Company",
        href: FINANCIAL_CONTRACTOR_COMPANIES_PAGE_ROUTE,
        icon: Building,
      },
      {
        label: "Price Lists",
        href: FINANCIAL_PRICE_LIST_PAGE_ROUTE,
        icon: ClipboardList,
      },
    ],
  },
  { label: "Files", href: FILES_PAGE_ROUTE, icon: FilesIcon },
]

export const WebViewerApiKey =
  // 'demo:1707733835290:7f4640f603000000007a6d458777d6529c56ca9278f8798b396c33c448'
  "Reznik Infrastructure Systems:OEM:ROG-Tech::B+:AMS(20250214):46A5232D0477260A8360B03AC9A2537860610FDDFBB35A65CB024BB21CCF60CE4CB231F5C7"

// export const ServerLicense =
//   'k7mvmZ+hmCGXw5RdDD7RNUEbS0avQl+LORVY/IsPNE0rPAPzr3RQNcy/66oKETi1V1bwi8qBzv1WaXO99DxKdWAQ4F9mWDJJ46xBTUgcQk+oTN//Ig3SbBg'
export const ServerLicense =
  "Reznik Infrastructure Systems:OEM:ROG-Tech::B+:AMS(20250214):46A5232D0477260A8360B03AC9A2537860610FDDFBB35A65CB024BB21CCF60CE4CB231F5C7"

export const xano_oauth_continue_url =
  "https://xk6s-eqwg-s8by.f2.xano.io/api:U0aE1wpF/oauth/google/continue"

export const redirect_uri = `https://rog-tech.io${LOGIN_PAGE_ROUTE}`

export const FileUploadBaseUrl = "https://rog-tech.io/files"
export const MAP_BOX_STYLE = "mapbox://styles/chann/clef9nc62000601pgkf94y02a"

export const REGEX_PHONE_NUMBER = new RegExp(
  /^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/,
)

export const BLOCKED_EXTENSIONS = [
  "com",
  "exe",
  "cmd",
  "bat",
  "dll",
  "lnk",
  "url",
  "js",
  "vbs",
  "ps1",
  "jar",
  "sh",
  "msi",
]

export const tabList = [
  { label: "All", icon: Home2, key: "all" },
  { label: "Files", icon: Folder, key: "file" },
  { label: "Projects", icon: ClipboardText, key: "project" },
]

export const taskViewOptions = [
  { id: "table", label: "Table", icon: Grid6 },
  { id: "kanban", label: "Grid", icon: Kanban },
  { id: "map", label: "Maps & info", icon: Map },
  { id: "finance", label: "Finance", icon: DollarSquare },
  { id: "analytics", label: "Analytics", icon: ChartSquare },
  { id: "work-diary", label: "Work Diary", icon: CalendarTick },
]

export const projectViewList = [
  { label: "Table", id: "table", icon: Grid6 },
  { label: "Card", id: "card", icon: Kanban },
]

export const notificationTabList = [
  { label: "All", value: "all" },
  { label: "Mentions", value: "mentioned" },
  { label: "Assigned to me", value: "assigned" },
]

export const taskTabsList = [
  { label: "Comments", value: 1 },
  { label: "Files", value: 2 },
  { label: "Activity logs", value: 3 },
]

export const defaultTaskColumnOrder = [
  "id",
  "name",
  "created_by.name",
  "notes",
  "description",
  "status",
  "priority",
  "assignees",
  "_gis",
  "files",
  "estimations",
  "dependencies",
  "sessions",
  "created_at",
  "actions",
]

export const defaultGlobalSearchPagination = {
  all: 1,
  file: 1,
  project: 1,
}

export const defaultGlobalSearchData = {
  all: { data: [], counter: 0 },
  file: { data: [], counter: 0 },
  project: { data: [], counter: 0 },
}

export const notificationTab: any[] = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "mentioned", label: "I was mentioned" },
  { value: "assigned", label: "Assigned to me" },
]

export const fileViewOptions = [
  { id: "table", label: "Table", icon: Grid6 },
  { id: "kanban", label: "Kanban", icon: Kanban },
]

export type SortOption = "type" | "name" | "modified" | "size"
export type SortDirection = "asc" | "desc"

export const sortFieldOptions: { value: string; labelKey: string; apiField: string }[] = [
  { value: "id", labelKey: "Ticket", apiField: "id" },
  { value: "title", labelKey: "Name", apiField: "title" },
  { value: "created_by", labelKey: "Created By", apiField: "created_by.name" },
  { value: "description", labelKey: "Description", apiField: "description" },
  { value: "status", labelKey: "Status", apiField: "status.name" },
  { value: "priority", labelKey: "Priority", apiField: "_priority.level" },
]

export const sortDirectionOptions: { value: SortDirection; labelKey: string }[] = [
  { value: "asc", labelKey: "A-Z" },
  { value: "desc", labelKey: "Z-A" },
]

export const menuItems = [
  { id: "project", label: "Project", icon: ClipboardText },
  { id: "group", label: "Group", icon: DocumentCopy },
  { id: "task", label: "Task", icon: TaskSquare },
  { id: "newFolder", label: "New Folder", icon: FolderCloud },
  { id: "uploadFolder", label: "Upload Folder", icon: Folder },
  { id: "uploadFile", label: "Upload File", icon: DocumentText1 },
]

export const quickActions: any[] = [
  { id: "share", label: "Share", icon: Share },
  { id: "copyLink", label: "Copy link", icon: Link1 },
  { id: "download", label: "Download", icon: DocumentDownload },
  { id: "cut", label: "Cut", icon: Cut },
  { id: "paste", label: "Paste", icon: ClipboardText },
  { id: "clearPaste", label: "Clear Paste", icon: ClipboardClose },
  { id: "copyTo", label: "Copy to", icon: DocumentCopy },
  { id: "rename", label: "Rename", icon: Edit2 },
  { id: "moveTo", label: "Move to", icon: ArrowRight },
  { id: "clear", label: "Clear", icon: CloseSquare },
  { id: "delete", label: "Delete", icon: Trash, danger: true },
]

export const settingList = [
  { label: "Workspace", id: "workspace", icon: Setting2 },
  { label: "Roles & Permissions", id: "role", icon: UserEdit },
  { label: "GIS", id: "gis", icon: Map },
  { label: "Printers", id: "printers", icon: Printer },
]

export const sections: any[] = [
  {
    title: "Permission Management",
    description: "Customize organization preferences",
    sub_description: "Note: Role-based permissions take precedence over organization preferences",
    settings: [
      {
        id: "allowGuests",
        label: "Allow guests to view projects",
        description: "Guest users can view but not edit projects",
      },
      {
        id: "requireApproval",
        label: "Require approval for new members",
        description: "Admin approval needed before granting access",
      },
      {
        id: "publicSharing",
        label: "Enable public project sharing",
        description: "Allow projects to be shared via public links",
      },
    ],
  },
  {
    title: "Projects",
    description:
      "These rules will be applied only to users without assigned roles, project view only members",
    settings: [
      {
        id: "project_view_only_members",
        label: "Projects",
        description: "project view only members",
        toolText: "Allows only project members to view the project",
      },
      {
        id: "owner_can_delete_project",
        label: "owner can delete project",
        description: "text",
        toolText: "Grants the project owner permission to delete the project",
      },
      {
        id: "project_edit_only_owner",
        label: "project edit only owner",
        description: "text",
        toolText: "Restricts project editing to the project owner only",
      },
      {
        id: "add_new_project_only_organization_account",
        label: "add new project only organization account",
        description: "text",
        toolText: "Allows only the organization account to create new projects",
      },
      {
        id: "share_projects",
        label: "share projects",
        description: "text",
        toolText: "Makes location mandatory for diary entries",
      },
      {
        id: "diary_location_mandatory",
        label: "diary location mandatory",
        description: "text",
        toolText: "Makes location mandatory for diary entries",
      },
    ],
  },
  {
    title: "Organization",
    description:
      "These rules will be applied only to users without assigned roles, project view only members",
    settings: [
      {
        id: "organization_accout_have_full_access",
        label: "organization account have full access",
        description: "text",
        toolText: "Grants full access to the organization account",
      },
      {
        id: "user_properties_edit_only_organization_account",
        label: "user properties edit only organization account",
        description: "text",
        toolText: "Allows only the organization account to edit user properties",
      },
      {
        id: "dictionary_edit_only_organization_account",
        label: "dictionary edit only organization account",
        description: "text",
        toolText: "Restricts dictionary editing to the organization account only",
      },
    ],
  },
  {
    title: "Files and folders",
    description: "These rules will be applied only to users without assigned roles",
    settings: [
      {
        id: "folders_managment_only_project_owner",
        label: "folders management only project owner",
        description: "text",
        toolText: "Allows only the project owner to manage folders",
      },
      {
        id: "file_delete_by_project_members",
        label: "file delete by project members",
        description: "text",
        toolText: "Permits project members to delete files",
      },
    ],
  },
  {
    title: "Notification settings",
    description: "These rules will be applied only to users without assigned roles",
    settings: [
      {
        id: "inform_by_email",
        label: "Inform by email",
        description: "text",
        toolText: "Enables email notifications",
      },
      {
        id: "inform_by_whatsapp",
        label: "Inform by whatsapp",
        description: "text",
        toolText: "Enables WhatsApp notifications",
      },
    ],
  },
]

export const settingBreadcrumbs = [
  {
    title: "Home",
    path: NEW_DASHBOARD_PAGE_ROUTE,
  },
  {
    title: "Settings",
    path: SETTING_PAGE_ROUTE,
  },
]
export const userProfileBreadcrumbs = [
  {
    title: "Home",
    path: NEW_DASHBOARD_PAGE_ROUTE,
  },
  {
    title: "Users",
    path: USER_PAGE_ROUTE,
  },
  {
    title: "User Profile",
    path: "",
  },
]
export const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "bmp",
  "ico",
  "tiff",
  "tif",
]
export const VIDEO_EXTENSIONS = [
  "mp4",
  "mov",
  "avi",
  "mkv",
  "webm",
  "flv",
  "wmv",
  "m4v",
  "mpeg",
  "mpg",
]
export const AUDIO_EXTENSIONS = ["mp3", "wav", "ogg", "flac", "aac", "m4a", "wma", "aiff"]
export const CODE_EXTENSIONS = [
  "js",
  "jsx",
  "ts",
  "tsx",
  "py",
  "java",
  "c",
  "cpp",
  "cs",
  "go",
  "rb",
  "php",
  "html",
  "css",
  "scss",
  "sass",
  "less",
  "json",
  "xml",
  "yaml",
  "yml",
  "sql",
  "sh",
  "bash",
  "ps1",
  "swift",
  "kt",
  "rs",
  "vue",
  "svelte",
]
export const TEXT_EXTENSIONS = ["txt", "md", "rtf", "doc", "docx", "odt", "log", "csv"]

export const defaultPermissions: Permission[] = [
  {
    id: "view_projects",
    name: "View Projects",
    description: "Can view all projects and their details",
    enabled: true,
  },
  {
    id: "edit_projects",
    name: "Edit Projects",
    description: "Can modify project information and settings",
    enabled: false,
  },
  {
    id: "manage_tasks",
    name: "Manage Tasks",
    description: "Can create, edit, and delete tasks",
    enabled: true,
  },
  {
    id: "admin_access",
    name: "Admin Access",
    description: "Full administrative access to the system",
    enabled: false,
  },
  {
    id: "view_financial_data",
    name: "View Financial Data",
    description: "Can view financial reports and budgets",
    enabled: false,
  },
  {
    id: "edit_financial_data",
    name: "Edit Financial Data",
    description: "Can modify financial information",
    enabled: true,
  },
  {
    id: "invite_users",
    name: "Invite Users",
    description: "Can send invitations to new team members",
    enabled: false,
  },
  {
    id: "manage_users",
    name: "Manage Users",
    description: "Can edit user roles and permissions",
    enabled: false,
  },
  {
    id: "view_reports",
    name: "View Reports",
    description: "Can access system reports and analytics",
    enabled: false,
  },
  {
    id: "export_data",
    name: "Export Data",
    description: "Can export data from the system",
    enabled: false,
  },
  {
    id: "gis_access",
    name: "GIS Access",
    description: "Can access and use GIS mapping features",
    enabled: false,
  },
  {
    id: "system_settings",
    name: "System Settings",
    description: "Can modify system-wide settings",
    enabled: false,
  },
]

export const defaultUserSettings = { email: "", phone: "", department: "", role: "" }

export const PRINTABLE_EXTENSIONS = ["jpg", "jpeg", "png", "pdf"]

/** Image file extensions for thumbnail preview */
export const OLD_IMAGE_EXTENSIONS = ["png", "webp", "jpeg", "jpg"]

/** Sorting field options */
export const SORTING_FIELDS = [
  { value: "name", label: "Name" },
  { value: "created_at", label: "Date Created" },
  { value: "last_update", label: "Date Modified" },
  { value: "size", label: "Size" },
]

/** Sorting order options */
export const SORTING_ORDERS = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
]

/** Max breadcrumbs before overflow */
export const BREADCRUMBS_OVERFLOW_LIMIT = 4
export const workDiaryTableColumns = [
  { id: "_company", title: "Contractor Company" },
  { id: "number", title: "Log Number" },
  { id: "diary_date", title: "Date" },
  { id: "contractor_approval", title: "Contractor" },
  { id: "supervisor_approval", title: "Supervisor" },
  { id: "_status", title: "Status" },
  { id: "action", title: "Action" },
]

export const profileTabs = [
  { id: "general", label: "General", icon: Setting2 },
  { id: "notification", label: "Notification", icon: Notification },
  { id: "user-permissions", label: "User Permissions", icon: UserTick },
  { id: "transfer-roles", label: "Transfer Roles & Access", icon: Convertshape },
]

export const CATEGORY_PAYLOAD_MAP: Record<string, string> = {
  owned_projects: "projects_owned_id",
  member_projects: "projects_membership_id",
  roles: "access_roles_id",
  finance_projects: "financial_roles_project_id",
  tasks_membership: "tasks_responsible_id",
  pinned_tasks: "pinned_tasks_id",
  pinned_projects: "pinned_projects_id",
}

export const defaultOrganizationSettings: Record<string, boolean> = {
  allowGuests: true,
  requireApproval: true,
  publicSharing: true,
  project_view_only_members: true,
  owner_can_delete_project: true,
  project_edit_only_owner: true,
  add_new_project_only_organization_account: true,
  organization_accout_have_full_access: true,
  user_properties_edit_only_organization_account: true,
  dictionary_edit_only_organization_account: true,
  folders_managment_only_project_owner: true,
  file_delete_by_project_members: true,
  inform_by_email: true,
  inform_by_whatsapp: true,
}

export const WorkDiaryStepsList = [
  { label: "General", description: "Configure work logs settings" },
  { label: "Resources", description: "Manage resources for the work log" },
  { label: "Tasks", description: "Manage tasks for the work log" },
  { label: "Comments & Files", description: "Manage comments and files for the work log" },
  { label: "Review & Save", description: "Review and confirm setup" },
]

export const userStatusList = [
  { id: 1, name: "Active" },
  { id: 3, name: "Inactive" },
  { id: 0, name: "Pending" },
]

export const financeTab = [
  { label: "Bill of Quantities", key: "boq" },
  { label: "Contractors", key: "contractors" },
  { label: "Price Lists", key: "price-lists" },
  { label: "Bills", key: "bills" },
]

export const COLUMN_TYPE_OPTIONS = [
  "Integer",
  "Decimal",
  "Text",
  "Boolean",
  "Checkbox",
  "Date",
  "Timestamp",
  "Range",
  "Selector",
  "File",
]

export const financeTabOption = [
  { label: "Bills", disabled: false },
  { label: "Roles", disabled: false },
  { label: "Quote Manager", disabled: false },
  { label: "Signature Requirements", disabled: false },
]

export const financeTabs: { label: string; value: NonNullable<SubTab>; disabled: boolean }[] = [
  { label: "Bills", value: "boq", disabled: false },
  { label: "Quote Manager", value: "quotes", disabled: true },
  { label: "Roles", value: "roles", disabled: false },
  { label: "Signature Requirements", value: "signatures", disabled: false },
]

export const TABLE_COLUMNS = [
  { id: "id", title: "Ticket Number", show: true },
  { id: "name", title: "Task Name", show: true },
  { id: "created_by.name", title: "Created By", show: true },
  { id: "notes", title: "Notes", show: true },
  { id: "description", title: "Description", show: true },
  { id: "status", title: "Status", show: true },
  { id: "priority", title: "Priority", show: true },
  { id: "assignees", title: "Assignee", show: true },
  { id: "_gis", title: "Gis", show: false },
  { id: "files", title: "Files", show: true },
  { id: "estimations", title: "Estimations", show: true },
  { id: "dependencies", title: "Dependencies", show: false },
  { id: "sessions", title: "Time", show: true },
  { id: "created_at", title: "Created At", show: true },
  { id: "actions", title: "Actions", show: true },
]

export const sortByOptions = [
  { value: "name", labelKey: "Name" },
  { value: "type", labelKey: "Type" },
  { value: "created_at", labelKey: "Modified" },
  { value: "size", labelKey: "Size" },
]

export const orderOptions = [
  { value: "asc", labelKey: "A-Z" },
  { value: "desc", labelKey: "Z-A" },
]

export const fileTypeOptions = [
  { value: "pdf", labelKey: "PDF" },
  { value: "word", labelKey: "Word" },
  { value: "excel", labelKey: "Excel" },
  { value: "cad", labelKey: "CAD" },
]

export const fileModifiedOptions = [
  { value: "today", labelKey: "Today" },
  { value: "last_7_days", labelKey: "Last 7 days" },
  { value: "last_30_days", labelKey: "Last 30 days" },
  { value: "this_year", labelKey: "This year" },
  { value: "custom_range", labelKey: "Custom range" },
]

export const fileSizeOptions = [
  { value: "lt_1mb", labelKey: "Under 1 MB" },
  { value: "1_10mb", labelKey: "1 - 10 MB" },
  { value: "10_50mb", labelKey: "10 - 50 MB" },
  { value: "50_1gb", labelKey: "50 MB - 1 GB" },
  { value: "over_1gb", labelKey: "Over 1 GB" },
]
// Module-level stable empty arrays — singleton refs to avoid re-render cascades
export const EMPTY_TASKS: any[] = []
export const EMPTY_PROJECTS: any[] = []

// FB-09: Custom column IDs for task progress tracking
export const KNOWN_INITIAL_COLUMN_IDS = [130, 132, 134, 136, 138, 140, 142, 144, 146, 148, 150, 152]
export const KNOWN_CURRENT_COLUMN_IDS = [131, 133, 135, 137, 139, 141, 143, 145, 147, 149, 151, 153]
export const INITIAL_COLUMN_NAME = "נתון התחלתי"
export const CURRENT_COLUMN_NAME = "נתון נוכחי"

export const CHART_PALETTE = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
  "#f97316",
  "#6366f1",
  "#14b8a6",
  "#e11d48",
  "#a855f7",
  "#0ea5e9",
  "#d946ef",
]

// Direction labels adapt to the selected sort field — dates get
// "Newest/Oldest First", sizes get "Largest/Smallest First", everything
// else falls back to the alphabetical A-Z / Z-A labels.
export const getOrderOptionsForField = (field: string) => {
  if (field === "size") {
    return [
      { value: "desc", labelKey: "Largest First" },
      { value: "asc", labelKey: "Smallest First" },
    ]
  }
  if (field === "created_at" || field === "updated_at") {
    return [
      { value: "desc", labelKey: "Newest First" },
      { value: "asc", labelKey: "Oldest First" },
    ]
  }
  return orderOptions
}

// ─── Notification Form ────────────────────────────────────────────────────────

export const NOTIFICATION_FREQUENCIES: {
  value: "daily" | "weekly" | "monthly"
  label: string
  subtitle: string
}[] = [
  { value: "daily", label: "Daily", subtitle: "Every day" },
  { value: "weekly", label: "Weekly", subtitle: "Once per week" },
  { value: "monthly", label: "Monthly", subtitle: "Once per month" },
]

// Day of week mapping per API spec (0 = Sunday ... 6 = Saturday)
export const DAYS_OF_WEEK: { label: string; value: number }[] = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
]

export const NOTIFICATION_METHODS = [
  {
    value: "whatsapp",
    label: "WhatsApp",
    subtitle: "Message with system link",
    titleClass: "text-success-700",
    subtitleClass: "text-success-600",
    activeBorder: "border-success-500 bg-success-50",
  },
  {
    value: "email",
    label: "Email",
    subtitle: 'With "Return to System" button',
    titleClass: "text-indigo-600",
    subtitleClass: "text-[#6172F3]",
    activeBorder: "border-indigo-500 bg-indigo-50",
  },
] as const

export const DEFAULT_FORM_VALUES: NotificationFormValues = {
  frequency: "daily",
  time: "",
  dayOfWeek: "",
  dayOfMonth: "",
  projectId: "",
  excludedUsers: [],
  excludedTasks: [],
  methods: [],
}

export const SORT_BY_OPTIONS = [
  { value: "created_at", label: "Created At" },
  { value: "start_at", label: "Start Date" },
  { value: "finish_to", label: "Finish Date" },
  { value: "title", label: "Title" },
]

export const ORDER_OPTIONS = [
  { value: "asc", label: "Ascending" },
  { value: "desc", label: "Descending" },
]

export const DEFAULT_SORT_BY = "created_at"
export const DEFAULT_ORDER_BY: "asc" | "desc" = "desc"

export const DEFAULT_FILTERS: ProjectFilters = {
  status_id: [],
  members_id: [],
  show_deleted: false,
  start_period: null,
  finish_period: null,
}

export const DEFAULT_SORTING: ProjectSorting = { orderBy: "desc", sortBy: "created_at" }
export const MOVE_DIALOG_QUERY_KEY = "move-dialog-folder-v2"

export const PAGE_SIZE_OPTIONS = [5, 10, 25, 30, 40, 50]

export const STATUS_VARIANT: Record<string, string> = {
  Pending: "bg-slate-300",
  Active: "bg-success",
  Review: "bg-primary/40",
  Deactivate: "bg-destructive",
}

// UI uses `created_at` for the "Modified" sort field, but the API expects `last_update`.
export const SORT_FIELD_ALIASES: Record<string, string> = {
  created_at: "last_update",
}

// Pre-defined size buckets in bytes — keys mirror fileSizeOptions in utils/constants.
export const SIZE_FILTER_RANGES: Record<string, { min: number; max: number }> = {
  lt_1mb: { min: 0, max: 1024 * 1024 },
  "1_10mb": { min: 1024 * 1024, max: 10 * 1024 * 1024 },
  "10_50mb": { min: 10 * 1024 * 1024, max: 50 * 1024 * 1024 },
  "50_1gb": { min: 50 * 1024 * 1024, max: 1024 * 1024 * 1024 },
  over_1gb: { min: 1024 * 1024 * 1024, max: Number.MAX_SAFE_INTEGER },
}

export const defaultContext: FolderFileContextParams = {
  isLoading: false,
  isFetching: false,
  searchFilter: "",
  sortingObj: null,
  folderList: [],
  setSortingObj: () => {},
  showDeletedItems: false,
  fileTypeFilter: [],
  modifiedFilter: "",
  modifiedDateRange: undefined,
  sizeFilter: "",
  uploadedByFilter: "",
  setSearchFilter: () => {},
  refetchFolderList: () => {},
  setShowDeletedItems: () => {},
  setFileTypeFilter: () => {},
  setModifiedFilter: () => {},
  setModifiedDateRange: () => {},
  setSizeFilter: () => {},
  setUploadedByFilter: () => {},
  currentFolderName: "",
  parentFolderId: null,
}

